import React, {useMemo} from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import {useParams} from 'react-router-dom';
import {LabelWithoutJson} from '@/components/atoms';
import {FONT_SIZE, FONT_WEIGHT} from '@/utils/constants/globalStyle';
import {COLOR_CODE} from '@/utils/constants/colors';
import PropTypes from 'prop-types';
import {id} from 'zod/v4/locales';
/**
 * PageHeader component displays a header with a title and optional children.
 * It also sets the page title using the `usePageTitle` hook.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title to display in the header.
 * @param {string} [props.wrapperClassName] - Additional class names to apply to the header container.
 * @param {string} [props.fontSize] - Font size for the title.
 * @param {string} [props.color] - Color for the title.
 *
 * @returns {JSX.Element} The rendered PageHeader component.
 */
const PageHeader = ({
  title,
  fontSize = FONT_SIZE.XXXXL,
  color = COLOR_CODE._EFEFEF,
  wrapperClassName = 'mb-5 mt-5'
}) => {
  const params = useParams();
  const pageTitle = useMemo(() => {
    const {id} = params;
    const pageSubContext = id ? ` [${id}]` : '';
    return title + pageSubContext;
  }, [params, title]);

  usePageTitle(pageTitle);
  return (
    <div
      className={`position-relative flex  items-center gap-4 font-bold ${wrapperClassName}`}>
      <LabelWithoutJson
        text={title}
        fontWeight={FONT_WEIGHT.BOLD}
        fontSize={fontSize}
        color={color}
        id={`page-header-title-${id}`}
      />
    </div>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
  color: PropTypes.string,
  wrapperClassName: PropTypes.string
};

export default React.memo(PageHeader);
